import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  HOLDER_ROLE,
  INVITATION_TTL_DAYS,
  canAccess,
  MEMBER_ROLES_BY_ACCOUNT,
  PRACTICE_ROLES,
  type PharmacyLink,
  type PracticeInvitation,
  type PracticeMember,
  type PublicPractice,
  type Role,
} from '@curentia/shared';
import { ErrorText, Field, GroupedCode, LockButton, Page, Submit, formatMobile } from '../components';
import { ACCOUNT_TYPE_LABELS, PRACTICE_TYPE_LABELS, ROLE_BLURBS, ROLE_LABELS, STATUS_LABELS, roleList } from '../lib/practices';
import { useLoad } from '../lib/useLoad';
import { useAction, useDevice, useSnapshot } from '../state';

const STATUS_NOTES: Record<PublicPractice['status'], string> = {
  pending: 'Our team is checking the practice’s registration. Records start to flow once it is verified. You can invite your team meanwhile.',
  active: '',
  rejected: 'Our verification team could not approve this practice.',
  suspended: 'This practice has been suspended. Nothing can be sent to or from it.',
};

export function PracticeScreen() {
  const { id = '' } = useParams();
  const device = useDevice();
  const { practicesVersion } = useSnapshot();
  // Bumped by this screen's own changes; live alerts bump practicesVersion for everyone else's.
  const [version, setVersion] = useState(0);
  const changed = () => setVersion((n) => n + 1);
  const { data, error } = useLoad(() => device.api.practice(id), [device, id, practicesVersion, version]);

  // A removed member's next load fails, so an error replaces what was shown before.
  if (error) {
    return (
      <Page actions={<LockButton />}>
        <h1>Practice</h1>
        <ErrorText error={error} />
        <div className="links">
          <Link to="/home">Back</Link>
        </div>
      </Page>
    );
  }
  if (!data) {
    return (
      <Page actions={<LockButton />}>
        <p className="muted">Loading…</p>
      </Page>
    );
  }
  const { practice, roles } = data;
  const admin = roles.includes('practice_admin');
  const working = practice.status === 'pending' || practice.status === 'active';
  return (
    <Page actions={<LockButton />}>
      <h1>{practice.name}</h1>
      <p className="muted">
        {PRACTICE_TYPE_LABELS[practice.type]} · {practice.city}
      </p>
      {practice.status === 'active' ? null : (
        <section className={`card ${practice.status === 'pending' ? 'warning' : 'danger'}`} aria-label="Practice status">
          <h2>{STATUS_LABELS[practice.status]}</h2>
          <p>{STATUS_NOTES[practice.status]}</p>
          {practice.statusReason ? <p>Reason: {practice.statusReason}</p> : null}
        </section>
      )}
      <p>
        Your roles: <strong data-testid="my-roles">{roleList(roles)}</strong>
      </p>
      {canAccess(roles, 'demographics') || canAccess(roles, 'prescriptions') ? (
        <div className="stack spaced-below">
          {canAccess(roles, 'demographics') ? (
            <Link to={`/practices/${practice.id}/patients`} className="button">
              Patients
            </Link>
          ) : null}
          {canAccess(roles, 'prescriptions') ? (
            <Link to={`/practices/${practice.id}/prescriptions`} className={`button${canAccess(roles, 'demographics') ? ' secondary' : ''}`}>
              Prescriptions
            </Link>
          ) : null}
          {roles.includes('pharmacist') ? (
            <Link to={`/practices/${practice.id}/register`} className="button secondary">
              Dispensing register
            </Link>
          ) : null}
        </div>
      ) : null}
      {practice.type !== 'pharmacy' && (admin || roles.includes('doctor')) ? <LetterheadCard practice={practice} roles={roles} onChange={changed} /> : null}
      <RecordsCard practice={practice} roles={roles} onChange={changed} />
      {admin ? <MembersCard practice={practice} version={version} onChange={changed} /> : null}
      {admin && working && practice.type !== 'solo' ? <InviteCard practice={practice} onChange={changed} /> : null}
      {admin ? <InvitationsCard practice={practice} version={version} onChange={changed} /> : null}
      {admin && practice.type === 'pharmacy' ? <DoctorLinksCard practice={practice} version={version} onChange={changed} /> : null}
      {practice.type === 'solo' ? null : <LeaveCard practice={practice} />}
      <div className="links">
        <Link to="/home">Back</Link>
      </div>
    </Page>
  );
}

const groups = (id: string) => (id.match(/.{1,4}/g) ?? []).join(' ');

/**
 * What the practice's prescriptions print at the top: its name, address and
 * phone, which the practice admin sets, and each doctor's qualifications,
 * which the doctor sets for their own account.
 */
function LetterheadCard({ practice, roles, onChange }: { practice: PublicPractice; roles: Role[]; onChange: () => void }) {
  const device = useDevice();
  const { account } = useSnapshot();
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState(practice.address ?? '');
  const [phone, setPhone] = useState(practice.phone ?? '');
  const [qualifications, setQualifications] = useState(account?.qualifications ?? '');
  const [editingMine, setEditingMine] = useState(false);
  const letterhead = useAction();
  const mine = useAction();
  const admin = roles.includes('practice_admin');
  const doctor = roles.includes('doctor');
  return (
    <section className="card" aria-labelledby="letterhead-heading">
      <h2 id="letterhead-heading">Prescription letterhead</h2>
      {editing ? (
        <form
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void letterhead.run(async () => {
              await device.api.setLetterhead(practice.id, { address: address.trim(), phone: phone.trim() });
              setEditing(false);
              onChange();
            });
          }}
          noValidate
        >
          <Field label="Address" value={address} maxLength={200} onChange={(e) => setAddress(e.target.value)} />
          <Field label="Phone" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <ErrorText error={letterhead.error} />
          <Submit busy={letterhead.busy} busyText="Saving…">
            Save the letterhead
          </Submit>
        </form>
      ) : (
        <>
          <p data-testid="letterhead">
            <strong>{practice.name}</strong>
            <br />
            {[practice.address, practice.city].filter(Boolean).join(', ')}
            {practice.phone ? (
              <>
                <br />
                Phone {practice.phone}
              </>
            ) : null}
          </p>
          {admin ? (
            <button
              type="button"
              className="button secondary small"
              onClick={() => {
                setAddress(practice.address ?? '');
                setPhone(practice.phone ?? '');
                setEditing(true);
              }}
            >
              Edit the address and phone
            </button>
          ) : null}
        </>
      )}
      {doctor && account ? (
        editingMine ? (
          <form
            className="spaced"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              void mine.run(async () => {
                await device.setQualifications(qualifications.trim());
                setEditingMine(false);
              });
            }}
            noValidate
          >
            <Field label="Your qualifications" hint="As printed under your name, such as MBBS, MD (Medicine)" maxLength={100} value={qualifications} onChange={(e) => setQualifications(e.target.value)} />
            <ErrorText error={mine.error} />
            <Submit busy={mine.busy} busyText="Saving…">
              Save your qualifications
            </Submit>
          </form>
        ) : (
          <div className="spaced">
            <p data-testid="my-qualifications">
              {account.name}
              {account.qualifications ? `, ${account.qualifications}` : ''}
              {account.registration ? (
                <>
                  <br />
                  <span className="muted">
                    Reg. no. {account.registration.number}, {account.registration.council}
                  </span>
                </>
              ) : null}
            </p>
            <button
              type="button"
              className="button secondary small"
              onClick={() => {
                setQualifications(account.qualifications ?? '');
                setEditingMine(true);
              }}
            >
              {account.qualifications ? 'Edit your qualifications' : 'Add your qualifications'}
            </button>
          </div>
        )
      ) : null}
    </section>
  );
}

function HubCard({ practice, roles, onChange }: { practice: PublicPractice; roles: Role[]; onChange: () => void }) {
  const device = useDevice();
  const { practicesVersion } = useSnapshot();
  const { data, error: loadError } = useLoad(() => device.api.practiceHub(practice.id), [device, practice.id, practicesVersion]);
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string } | null>(null);
  const [unpairing, setUnpairing] = useState(false);
  const { busy, error, run } = useAction();
  const admin = roles.includes('practice_admin');
  const working = practice.status === 'pending' || practice.status === 'active';
  const hub = data?.hub ?? null;
  return (
    <section className="card" aria-labelledby="hub-heading">
      <h2 id="hub-heading">Records</h2>
      <p>The practice’s records are kept on its hub PC, and members’ devices fetch them from there.</p>
      <ErrorText error={loadError} />
      {!data && !loadError ? <p className="muted">Loading…</p> : null}
      {hub ? (
        <>
          <p data-testid="record-holder">
            Hub: <strong>{hub.label}</strong>, paired {new Date(hub.pairedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
          <p className="muted">Its fingerprint, to check against the hub PC:</p>
          <GroupedCode code={groups(hub.deviceId)} separator=" " label="Hub fingerprint" testId="hub-fingerprint" />
        </>
      ) : data ? (
        <p className="muted" data-testid="record-holder">
          No hub is paired yet.
        </p>
      ) : null}
      {pairing ? (
        <div className="stack" role="status">
          <p>On the clinic PC, install the Curentia hub and run this within an hour. The code works once.</p>
          <pre className="command" data-testid="pair-command">{`curentia-hub pair --server ${device.api.baseUrl} --code ${pairing.code}`}</pre>
          <GroupedCode code={pairing.code} label="Hub pairing code" testId="hub-pairing-code" />
          <button type="button" className="button secondary small" onClick={() => setPairing(null)}>
            Done
          </button>
        </div>
      ) : null}
      <ErrorText error={error} />
      {admin && working && !pairing ? (
        <div className="actions">
          <button type="button" className="button small" disabled={busy} onClick={() => void run(async () => setPairing(await device.api.createHubPairing(practice.id)))}>
            {hub ? 'Pair a replacement hub' : 'Pair a hub'}
          </button>
          {hub && !unpairing ? (
            <button type="button" className="button danger small" onClick={() => setUnpairing(true)}>
              Unpair the hub
            </button>
          ) : null}
        </div>
      ) : null}
      {unpairing ? (
        <div className="stack">
          <p>Unpairing cuts the hub PC off at once, for example when it is lost. Members cannot reach the records until a hub is paired again.</p>
          <button
            type="button"
            className="button danger"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await device.api.unpairHub(practice.id);
                setUnpairing(false);
                onChange();
              })
            }
          >
            Unpair the hub now
          </button>
          <button type="button" className="button secondary" onClick={() => setUnpairing(false)}>
            Keep it
          </button>
        </div>
      ) : null}
      {hub && admin ? <p className="muted">Pairing a replacement retires the current hub.</p> : null}
    </section>
  );
}

function RecordsCard({ practice, roles, onChange }: { practice: PublicPractice; roles: Role[]; onChange: () => void }) {
  const device = useDevice();
  const { deviceId } = useSnapshot();
  const { busy, error, run } = useAction();
  if (practice.recordHolder === 'hub') return <HubCard practice={practice} roles={roles} onChange={onChange} />;
  const here = deviceId !== null && practice.recordHolderDeviceIds.includes(deviceId);
  const canHold = roles.includes('practice_admin') && roles.includes(HOLDER_ROLE[practice.type]);
  return (
    <section className="card">
      <h2>Records</h2>
      <p data-testid="record-holder">
        {here
          ? 'The practice’s records are kept on this device.'
          : practice.recordHolderDeviceIds.length > 0
            ? 'The practice’s records are kept on another device.'
            : 'No device keeps the practice’s records yet.'}
      </p>
      {canHold && !here ? (
        <>
          <p className="muted">Records already on another device stay there. Restoring a backup on this device keeps the same keys instead.</p>
          <ErrorText error={error} />
          <button
            type="button"
            className="button secondary"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await device.holdPracticeRecords(practice.id);
                onChange();
              })
            }
          >
            Keep the records on this device
          </button>
        </>
      ) : null}
    </section>
  );
}

interface CardProps {
  practice: PublicPractice;
  version: number;
  onChange: () => void;
}

function MembersCard({ practice, version, onChange }: CardProps) {
  const device = useDevice();
  const { account, practicesVersion } = useSnapshot();
  const { data, error } = useLoad(() => device.api.members(practice.id), [device, practice.id, practicesVersion, version]);
  return (
    <section className="card" aria-labelledby="members-heading">
      <h2 id="members-heading">Members</h2>
      <ErrorText error={error} />
      {!data && !error ? <p className="muted">Loading…</p> : null}
      {data ? (
        <ul className="list" aria-label="Members">
          {data.members.map((member) => (
            <MemberRow
              key={member.accountId}
              practice={practice}
              member={member}
              self={member.accountId === account?.id}
              onChange={onChange}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function MemberRow({ practice, member, self, onChange }: { practice: PublicPractice; member: PracticeMember; self: boolean; onChange: () => void }) {
  const device = useDevice();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [roles, setRoles] = useState<Role[]>(member.roles);
  const { busy, error, run } = useAction();
  const options = PRACTICE_ROLES[practice.type].filter((role) => MEMBER_ROLES_BY_ACCOUNT[member.accountType].includes(role));
  const toggle = (role: Role) => setRoles(roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role]);

  return (
    <li className="member" aria-label={member.name}>
      <div>
        <strong>{member.name}</strong>
        {self ? <span className="muted"> (you)</span> : null}
        <div className="muted">
          {ACCOUNT_TYPE_LABELS[member.accountType]} · {formatMobile(member.mobile)}
          {member.registration ? ` · ${member.registration.council} ${member.registration.number}` : null}
        </div>
        <div>{roleList(member.roles)}</div>
      </div>
      {editing ? (
        <fieldset className="choices compact">
          <legend>Roles for {member.name}</legend>
          {options.map((role) => (
            <label key={role} className="check">
              <input type="checkbox" checked={roles.includes(role)} onChange={() => toggle(role)} />
              {ROLE_LABELS[role]}
            </label>
          ))}
          <ErrorText error={error} />
          <div className="actions">
            <button
              type="button"
              className="button small"
              disabled={busy || roles.length === 0}
              onClick={() =>
                void run(async () => {
                  await device.api.changeRoles(practice.id, member.accountId, roles);
                  setEditing(false);
                  onChange();
                })
              }
            >
              Save roles
            </button>
            <button type="button" className="button secondary small" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </fieldset>
      ) : confirming ? (
        <div className="stack">
          <p>Remove {member.name}? Their access to the practice ends at once.</p>
          <ErrorText error={error} />
          <div className="actions">
            <button
              type="button"
              className="button danger small"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await device.api.removeMember(practice.id, member.accountId);
                  onChange();
                })
              }
            >
              Remove {member.name}
            </button>
            <button type="button" className="button secondary small" onClick={() => setConfirming(false)}>
              Keep
            </button>
          </div>
        </div>
      ) : practice.type === 'solo' ? null : (
        <div className="actions">
          {options.length > 1 ? (
            <button
              type="button"
              className="button secondary small"
              onClick={() => {
                setRoles(member.roles);
                setEditing(true);
              }}
            >
              Change roles
            </button>
          ) : null}
          {self ? null : (
            <button type="button" className="button danger small" onClick={() => setConfirming(true)}>
              Remove
            </button>
          )}
        </div>
      )}
    </li>
  );
}

function InviteCard({ practice, onChange }: { practice: PublicPractice; onChange: () => void }) {
  const device = useDevice();
  const [roles, setRoles] = useState<Role[]>([]);
  const [label, setLabel] = useState('');
  const [made, setMade] = useState<{ code: string; label: string } | null>(null);
  const { busy, error, setError, run } = useAction();
  const toggle = (role: Role) => setRoles(roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (roles.length === 0) return setError('Choose at least one role.');
    void run(async () => {
      const { code } = await device.api.invite(practice.id, roles, label.trim() || undefined);
      setMade({ code, label: label.trim() });
      setRoles([]);
      setLabel('');
      onChange();
    });
  };

  return (
    <section className="card" aria-labelledby="invite-heading">
      <h2 id="invite-heading">Invite someone</h2>
      {made ? (
        <div className="stack" role="status">
          <p>Give this code to {made.label || 'the person you are inviting'}. It works once, within {INVITATION_TTL_DAYS} days. You approve them after they use it.</p>
          <GroupedCode code={made.code} label="Invitation code" testId="invitation-code" />
          <button type="button" className="button secondary small" onClick={() => setMade(null)}>
            Invite someone else
          </button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <fieldset className="choices compact">
            <legend>Their roles</legend>
            {PRACTICE_ROLES[practice.type].map((role) => (
              <label key={role} className="check">
                <input type="checkbox" checked={roles.includes(role)} onChange={() => toggle(role)} />
                <span>
                  {ROLE_LABELS[role]}
                  {ROLE_BLURBS[role] ? <span className="muted"> · {ROLE_BLURBS[role]}</span> : null}
                </span>
              </label>
            ))}
          </fieldset>
          <Field label="Who is it for? (optional)" value={label} onChange={(e) => setLabel(e.target.value)} hint="Only you see this note." />
          <ErrorText error={error} />
          <Submit busy={busy} busyText="Creating…">
            Create an invitation code
          </Submit>
        </form>
      )}
    </section>
  );
}

function InvitationsCard({ practice, version, onChange: changed }: CardProps) {
  const device = useDevice();
  const { practicesVersion } = useSnapshot();
  const { data, error } = useLoad(() => device.api.invitations(practice.id), [device, practice.id, practicesVersion, version]);
  if (!data || data.invitations.length === 0) return error ? <ErrorText error={error} /> : null;
  const used = data.invitations.filter((i) => i.status === 'accepted');
  const open = data.invitations.filter((i) => i.status === 'open');
  return (
    <section className="card" aria-labelledby="invitations-heading">
      <h2 id="invitations-heading">Invitations</h2>
      {used.length > 0 ? (
        <>
          <h3>Waiting for your approval</h3>
          <ul className="list" aria-label="Waiting for your approval">
            {used.map((invitation) => (
              <UsedInvitation key={invitation.id} practice={practice} invitation={invitation} onChange={changed} />
            ))}
          </ul>
        </>
      ) : null}
      {open.length > 0 ? (
        <>
          <h3>Codes not used yet</h3>
          <ul className="list" aria-label="Codes not used yet">
            {open.map((invitation) => (
              <OpenInvitation key={invitation.id} practice={practice} invitation={invitation} onChange={changed} />
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

function UsedInvitation({ practice, invitation, onChange }: { practice: PublicPractice; invitation: PracticeInvitation; onChange: () => void }) {
  const device = useDevice();
  const { busy, error, run } = useAction();
  const who = invitation.acceptedBy;
  if (!who) return null;
  return (
    <li className="member" aria-label={who.name}>
      <div>
        <strong>{who.name}</strong>
        <div className="muted">
          {ACCOUNT_TYPE_LABELS[who.accountType]}
          {who.registration ? ` · ${who.registration.council} ${who.registration.number}` : null}
          {invitation.label ? ` · your note: ${invitation.label}` : null}
        </div>
        <div>Asked to join as {roleList(invitation.roles).toLowerCase()}</div>
      </div>
      <ErrorText error={error} />
      <div className="actions">
        <button
          type="button"
          className="button small"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await device.api.approveInvitation(practice.id, invitation.id);
              onChange();
            })
          }
        >
          Approve {who.name}
        </button>
        <button
          type="button"
          className="button secondary small"
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await device.api.cancelInvitation(practice.id, invitation.id);
              onChange();
            })
          }
        >
          Decline
        </button>
      </div>
    </li>
  );
}

function OpenInvitation({ practice, invitation, onChange }: { practice: PublicPractice; invitation: PracticeInvitation; onChange: () => void }) {
  const device = useDevice();
  const { busy, error, run } = useAction();
  return (
    <li className="member">
      <div>
        <strong>{invitation.label ?? 'Invitation code'}</strong>
        <div className="muted">
          {roleList(invitation.roles)} · until {new Date(invitation.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
      </div>
      <ErrorText error={error} />
      <button
        type="button"
        className="button secondary small"
        disabled={busy}
        onClick={() =>
          void run(async () => {
            await device.api.cancelInvitation(practice.id, invitation.id);
            onChange();
          })
        }
      >
        Cancel code
      </button>
    </li>
  );
}

function DoctorLinksCard({ practice, version, onChange: changed }: CardProps) {
  const device = useDevice();
  const { practicesVersion } = useSnapshot();
  const { data, error } = useLoad(() => device.api.linksToPharmacy(practice.id), [device, practice.id, practicesVersion, version]);
  return (
    <section className="card" aria-labelledby="links-heading">
      <h2 id="links-heading">Doctors sending prescriptions here</h2>
      <p className="muted">A linked doctor can send prescriptions straight to this pharmacy. Patients can still choose any pharmacy.</p>
      <ErrorText error={error} />
      {data && data.links.length === 0 ? <p className="muted">No doctor has asked yet.</p> : null}
      {data && data.links.length > 0 ? (
        <ul className="list" aria-label="Doctor links">
          {data.links.map((link) => (
            <DoctorLink key={link.id} link={link} onChange={changed} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function DoctorLink({ link, onChange }: { link: PharmacyLink; onChange: () => void }) {
  const device = useDevice();
  const { busy, error, run } = useAction();
  const act = (action: () => Promise<unknown>) =>
    void run(async () => {
      await action();
      onChange();
    });
  return (
    <li className="member" aria-label={link.doctor.name}>
      <div>
        <strong>{link.doctor.name}</strong>
        <div className="muted">
          {link.doctor.registration ? `${link.doctor.registration.council} ${link.doctor.registration.number} · ` : null}
          {link.status === 'active' ? 'Linked' : 'Asked to link'}
        </div>
      </div>
      <ErrorText error={error} />
      <div className="actions">
        {link.status === 'requested' ? (
          <button type="button" className="button small" disabled={busy} onClick={() => act(() => device.api.acceptPharmacyLink(link.id))}>
            Accept {link.doctor.name}
          </button>
        ) : null}
        <button type="button" className="button secondary small" disabled={busy} onClick={() => act(() => device.api.removePharmacyLink(link.id))}>
          {link.status === 'requested' ? 'Decline' : 'Remove link'}
        </button>
      </div>
    </li>
  );
}

function LeaveCard({ practice }: { practice: PublicPractice }) {
  const device = useDevice();
  const navigate = useNavigate();
  const { account } = useSnapshot();
  const [confirming, setConfirming] = useState(false);
  const { busy, error, run } = useAction();
  if (!account) return null;
  return (
    <section className="card">
      <h2>Leave the practice</h2>
      {confirming ? (
        <div className="stack">
          <p>You will lose access to {practice.name} at once. Its admin can invite you again.</p>
          <ErrorText error={error} />
          <button
            type="button"
            className="button danger"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await device.api.removeMember(practice.id, account.id);
                navigate('/home');
              })
            }
          >
            Leave {practice.name}
          </button>
          <button type="button" className="button secondary" onClick={() => setConfirming(false)}>
            Stay
          </button>
        </div>
      ) : (
        <button type="button" className="button secondary" onClick={() => setConfirming(true)}>
          Leave this practice
        </button>
      )}
    </section>
  );
}
